/**
 * Purchase Tax Register Screen (4.6.2)
 * Procurement Module - Reports
 * UI Layout: EXACT MATCH to PDF specification
 * 
 * Layout: Filter section with date range, tax type
 * Grid: Tax-wise purchase report with CGST/SGST/IGST breakdown
 */

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, Download, FileSpreadsheet, FileText, Search, Filter } from 'lucide-react';

const PurchaseTaxRegisterScreen = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  
  // Filter state
  const [filters, setFilters] = useState({
    fromDate: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    supplier: '',
    taxType: 'All',
    taxPercent: 'All',
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Totals
  const [totals, setTotals] = useState({
    taxableValue: '0.00',
    cgstAmt: '0.00',
    sgstAmt: '0.00',
    igstAmt: '0.00',
    totalGst: '0.00',
  });

  // Theme colors
  const bgColor = isDark ? 'bg-[#1a1a2e]' : 'bg-gray-100';
  const cardBg = isDark ? 'bg-[#16213e]' : 'bg-white';
  const inputBg = isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50';
  const borderColor = isDark ? 'border-[#1f4068]' : 'border-gray-300';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const labelColor = isDark ? 'text-gray-300' : 'text-gray-700';
  const sectionBg = isDark ? 'bg-[#1f2937]' : 'bg-gray-50';

  // Load report data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const reportData = [
        { id: 1, poNo: 'PO101L0001', poDate: '2024-01-15', supplier: 'FASHION FOREC', gstin: '27AACPC7917J1ZO', hsnCode: '6205', taxableValue: '213750.00', taxPercent: '12', cgstPercent: '6', cgstAmt: '6412.50', sgstPercent: '6', sgstAmt: '6412.50', igstPercent: '0', igstAmt: '0.00' },
        { id: 2, poNo: 'PO101L0002', poDate: '2024-01-16', supplier: 'BALAJI ENTERPRISES', gstin: '36AATFB6624J1Z0', hsnCode: '6206', taxableValue: '114000.00', taxPercent: '12', cgstPercent: '0', cgstAmt: '0.00', sgstPercent: '0', sgstAmt: '0.00', igstPercent: '12', igstAmt: '13680.00' },
        { id: 3, poNo: 'PO101L0003', poDate: '2024-01-18', supplier: 'KRISHNA TEXTILES', gstin: '36AABCT1234A1Z5', hsnCode: '6109', taxableValue: '285000.00', taxPercent: '5', cgstPercent: '2.5', cgstAmt: '3562.50', sgstPercent: '2.5', sgstAmt: '3562.50', igstPercent: '0', igstAmt: '0.00' },
      ];
      
      setData(reportData);
      
      // Calculate totals
      const totalTaxable = reportData.reduce((sum, item) => sum + parseFloat(item.taxableValue), 0);
      const totalCgst = reportData.reduce((sum, item) => sum + parseFloat(item.cgstAmt), 0);
      const totalSgst = reportData.reduce((sum, item) => sum + parseFloat(item.sgstAmt), 0);
      const totalIgst = reportData.reduce((sum, item) => sum + parseFloat(item.igstAmt), 0);
      
      setTotals({
        taxableValue: totalTaxable.toFixed(2),
        cgstAmt: totalCgst.toFixed(2),
        sgstAmt: totalSgst.toFixed(2),
        igstAmt: totalIgst.toFixed(2),
        totalGst: (totalCgst + totalSgst + totalIgst).toFixed(2),
      });
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExportExcel = () => {
    console.log('Exporting to Excel...');
    alert('Export to Excel functionality');
  };

  const handleExportPDF = () => {
    console.log('Exporting to PDF...');
    alert('Export to PDF functionality');
  };

  // Input field component
  const InputField = ({ label, name, value, onChange, type = 'text' }) => (
    <div className="flex items-center">
      <label className={`w-20 text-xs ${labelColor}`}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
        data-testid={`input-${name}`}
      />
    </div>
  );

  const SelectField = ({ label, name, value, onChange, options }) => (
    <div className="flex items-center">
      <label className={`w-20 text-xs ${labelColor}`}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
        data-testid={`select-${name}`}
      >
        {options.map(opt => (
          <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} p-4`} data-testid="purchase-tax-register-screen">
      {/* Header */}
      <div className={`${cardBg} rounded-lg p-3 mb-4 border ${borderColor}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">Purchase Tax Register</h1>
            <p className="text-xs text-gray-400">Screen ID: 4.6.2 | Procurement Module</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white" data-testid="refresh-btn">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={handleExportExcel} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm">
              <FileSpreadsheet size={16} /> Excel
            </button>
            <button onClick={handleExportPDF} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-sm">
              <FileText size={16} /> PDF
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2">
          <AlertCircle className="text-red-400" size={18} />
          <span className="text-red-400 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">×</button>
        </div>
      )}

      {/* Filters */}
      <div className={`${cardBg} rounded-lg border ${borderColor} p-4 mb-4`}>
        <div className={`${sectionBg} rounded-lg p-3 border ${borderColor}`}>
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} className="text-blue-400" />
            <h3 className="text-xs font-semibold">Report Filters</h3>
          </div>
          <div className="grid grid-cols-5 gap-4">
            <InputField label="From Date" name="fromDate" value={filters.fromDate} onChange={(n, v) => setFilters(p => ({ ...p, [n]: v }))} type="date" />
            <InputField label="To Date" name="toDate" value={filters.toDate} onChange={(n, v) => setFilters(p => ({ ...p, [n]: v }))} type="date" />
            <div className="flex items-center">
              <label className={`w-20 text-xs ${labelColor}`}>Supplier</label>
              <div className="flex-1 flex gap-1">
                <input
                  type="text"
                  value={filters.supplier}
                  onChange={(e) => setFilters(p => ({ ...p, supplier: e.target.value }))}
                  className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
                  placeholder="All Suppliers"
                />
                <button className={`p-1 rounded border ${borderColor} ${inputBg}`}>
                  <Search size={12} className="text-gray-400" />
                </button>
              </div>
            </div>
            <SelectField 
              label="Tax Type" name="taxType" value={filters.taxType} 
              onChange={(n, v) => setFilters(p => ({ ...p, [n]: v }))} 
              options={['All', 'Inter State (IGST)', 'Intra State (CGST+SGST)']} 
            />
            <SelectField 
              label="Tax %" name="taxPercent" value={filters.taxPercent} 
              onChange={(n, v) => setFilters(p => ({ ...p, [n]: v }))} 
              options={['All', '0%', '5%', '12%', '18%', '28%']} 
            />
          </div>
          <div className="flex justify-end mt-3">
            <button onClick={loadData} className="px-4 py-1.5 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white">
              Generate Report
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4 mb-4">
        <div className={`${cardBg} rounded-lg p-3 border ${borderColor}`}>
          <p className={`text-xs ${labelColor}`}>Taxable Value</p>
          <p className="text-xl font-bold">₹{totals.taxableValue}</p>
        </div>
        <div className={`${cardBg} rounded-lg p-3 border ${borderColor}`}>
          <p className={`text-xs ${labelColor}`}>CGST Amount</p>
          <p className="text-xl font-bold text-blue-400">₹{totals.cgstAmt}</p>
        </div>
        <div className={`${cardBg} rounded-lg p-3 border ${borderColor}`}>
          <p className={`text-xs ${labelColor}`}>SGST Amount</p>
          <p className="text-xl font-bold text-purple-400">₹{totals.sgstAmt}</p>
        </div>
        <div className={`${cardBg} rounded-lg p-3 border ${borderColor}`}>
          <p className={`text-xs ${labelColor}`}>IGST Amount</p>
          <p className="text-xl font-bold text-orange-400">₹{totals.igstAmt}</p>
        </div>
        <div className={`${cardBg} rounded-lg p-3 border ${borderColor}`}>
          <p className={`text-xs ${labelColor}`}>Total GST</p>
          <p className="text-xl font-bold text-emerald-400">₹{totals.totalGst}</p>
        </div>
      </div>

      {/* Report Grid */}
      <div className={`${cardBg} rounded-lg border ${borderColor} overflow-hidden`}>
        <div className={`p-3 border-b ${borderColor}`}>
          <h3 className="text-sm font-semibold">Tax Register Details</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="data-table">
            <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
              <tr>
                <th className="px-2 py-2 text-left font-semibold">Sl.No</th>
                <th className="px-2 py-2 text-left font-semibold">PO No</th>
                <th className="px-2 py-2 text-left font-semibold">PO Date</th>
                <th className="px-2 py-2 text-left font-semibold">Supplier</th>
                <th className="px-2 py-2 text-left font-semibold">GSTIN</th>
                <th className="px-2 py-2 text-left font-semibold">HSN</th>
                <th className="px-2 py-2 text-right font-semibold">Taxable Value</th>
                <th className="px-2 py-2 text-right font-semibold">Tax %</th>
                <th className="px-2 py-2 text-right font-semibold">CGST %</th>
                <th className="px-2 py-2 text-right font-semibold">CGST Amt</th>
                <th className="px-2 py-2 text-right font-semibold">SGST %</th>
                <th className="px-2 py-2 text-right font-semibold">SGST Amt</th>
                <th className="px-2 py-2 text-right font-semibold">IGST %</th>
                <th className="px-2 py-2 text-right font-semibold">IGST Amt</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr key={item.id} className={`border-t ${borderColor} hover:${isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50'}`}>
                  <td className="px-2 py-2">{index + 1}</td>
                  <td className="px-2 py-2 font-mono text-blue-400">{item.poNo}</td>
                  <td className="px-2 py-2">{item.poDate}</td>
                  <td className="px-2 py-2">{item.supplier}</td>
                  <td className="px-2 py-2 font-mono text-xs">{item.gstin}</td>
                  <td className="px-2 py-2">{item.hsnCode}</td>
                  <td className="px-2 py-2 text-right">{item.taxableValue}</td>
                  <td className="px-2 py-2 text-right">{item.taxPercent}%</td>
                  <td className="px-2 py-2 text-right">{item.cgstPercent}%</td>
                  <td className="px-2 py-2 text-right text-blue-400">{item.cgstAmt}</td>
                  <td className="px-2 py-2 text-right">{item.sgstPercent}%</td>
                  <td className="px-2 py-2 text-right text-purple-400">{item.sgstAmt}</td>
                  <td className="px-2 py-2 text-right">{item.igstPercent}%</td>
                  <td className="px-2 py-2 text-right text-orange-400">{item.igstAmt}</td>
                </tr>
              ))}
              {/* Totals Row */}
              <tr className={`border-t-2 ${borderColor} font-semibold ${isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}`}>
                <td colSpan={6} className="px-2 py-2 text-right">Total:</td>
                <td className="px-2 py-2 text-right">{totals.taxableValue}</td>
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right text-blue-400">{totals.cgstAmt}</td>
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right text-purple-400">{totals.sgstAmt}</td>
                <td className="px-2 py-2"></td>
                <td className="px-2 py-2 text-right text-orange-400">{totals.igstAmt}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PurchaseTaxRegisterScreen;
